import { CampaignLog } from "@bt-common/schemas/bt/campaign-log";
import { Badge } from "@/components/ui/badge";
import moment from "moment";
import { Separator } from "@/components/ui/separator";
import { formatDelta } from "@common/utils/time-format-utils";
import { extractPhoneNumber } from "@common/utils/whatsapp-id-utils";
import { COPY_FEEDBACK_TIMEOUT_MS } from "@common/constants/timeout-constants";
import { Copy, AlertCircle, ChevronDown, ChevronUp, WifiOff, QrCode, CheckCircle2, Play, Pause, XCircle, StopCircle, Octagon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useState, useMemo } from "react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { useAppSelector } from "@common-reducers/hooks/store.hook";
import { selectWhatsappContactName } from "@common-reducers/WhatsAppSelectors";
import ContactAvatar from "@extension/context/content/components/bulk-dashboard/contact-table/contact-avatar";

interface CampaignTimelineItemProps {
    log: CampaignLog;
    isLast?: boolean;
    isFirst?: boolean;
    previousLogTimestamp?: string | Date;
}

// formatDelta is now imported from @common/utils/time-format-utils

function CampaignStatusBadge({ status, logMessage }: { status: string; logMessage?: string }) {
    // Check for "finished generating" event - should match "started" styling with stop icon
    if (status.includes('generation-complete') || logMessage?.toLowerCase().includes('finished generating')) {
        return (
            <Badge variant="outline" className="text-xs px-2 py-0.5 bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-300 border-blue-300 dark:border-blue-700">
                <Octagon className="h-3 w-3 mr-1" />
                <span>Finished Generating</span>
            </Badge>
        );
    }
    if (status.includes('completed') || status.includes('delivered')) {
        return (
            <Badge variant="outline" className="text-xs px-2 py-0.5 bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-300 border-blue-300 dark:border-blue-700">
                <StopCircle className="h-3 w-3 mr-1" />
                <span>{status.replace(/-/g, ' ')}</span>
            </Badge>
        );
    }
    if (status.includes('paused')) {
        return (
            <Badge variant="outline" className="text-xs px-2 py-0.5 bg-yellow-100 dark:bg-yellow-900/50 text-yellow-800 dark:text-yellow-300 border-yellow-300 dark:border-yellow-700">
                <Pause className="h-3 w-3 mr-1" />
                <span>Paused</span>
            </Badge>
        );
    }
    if (status.includes('aborted') || status.includes('error')) {
        return (
            <Badge variant="outline" className="text-xs px-2 py-0.5 bg-red-100 dark:bg-red-900/50 text-red-800 dark:text-red-300 border-red-300 dark:border-red-700">
                <XCircle className="h-3 w-3 mr-1" />
                <span>{status.replace(/-/g, ' ')}</span>
            </Badge>
        );
    }
    if (status.includes('started') || status.includes('running')) {
        return (
            <Badge variant="outline" className="text-xs px-2 py-0.5 bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-300 border-blue-300 dark:border-blue-700">
                <Play className="h-3 w-3 mr-1" />
                <span>Started</span>
            </Badge>
        );
    }
    
    return (
        <Badge variant="outline" className="text-xs px-2 py-0.5 bg-slate-100 dark:bg-slate-700 text-slate-800 dark:text-slate-200 border-slate-300 dark:border-slate-600">
            <span>{status.replace(/-/g, ' ')}</span>
        </Badge>
    );
}

export function CampaignTimelineItem({ log, isLast = false, isFirst = false, previousLogTimestamp }: CampaignTimelineItemProps) {
    const [copiedId, setCopiedId] = useState<string | null>(null);
    const [isDetailsExpanded, setIsDetailsExpanded] = useState(false);
    
    const deltaText = previousLogTimestamp && log.createdAt ? formatDelta(previousLogTimestamp, log.createdAt) : null;
    
    const hasError = !!log.metadata?.error;
    const errorMessage = hasError 
        ? (typeof log.metadata?.error === 'string' ? log.metadata.error : JSON.stringify(log.metadata?.error))
        : null;
    
    const logMessage = log.metadata?.logMessage || log.status;
    
    // Contact information for campaign message created logs
    const contact = log.metadata?.contact;
    const contactWhatsappId = contact?.whatsappId;
    const contactName = useMemo(() => {
        if (!contact) return null;
        return contact.name ||
               [contact.firstName, contact.lastName].filter(Boolean).join(' ') ||
               contact.mobile ||
               extractPhoneNumber(contactWhatsappId) ||
               'Unknown Contact';
    }, [contact, contactWhatsappId]);
    
    // Get contact display name from store if available, otherwise use metadata
    const contactDisplayName = useAppSelector((state) => 
        contactWhatsappId ? selectWhatsappContactName(state, contactWhatsappId) : null
    );
    
    // Use display name from store if available, otherwise use metadata
    const finalContactName = contactDisplayName || contactName;

    const handleCopyId = async (e: React.MouseEvent, id: string) => {
        e.stopPropagation();
        await navigator.clipboard.writeText(id);
        setCopiedId(id);
        setTimeout(() => setCopiedId(null), COPY_FEEDBACK_TIMEOUT_MS);
    };

    return (
        <div className="relative flex gap-4 pb-6">
            {/* Timeline line */}
            {!isLast && (
                <div className="absolute left-[11px] top-8 bottom-0 w-0.5 bg-slate-200 dark:bg-slate-700" />
            )}
            
            {/* Timeline dot */}
            <div className="relative z-10 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-slate-100 dark:bg-slate-700 border-2 border-slate-300 dark:border-slate-600">
                <div className="h-2 w-2 rounded-full bg-slate-400 dark:bg-slate-500" />
            </div>

            {/* Main Content */}
            <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-4 mb-2">
                    {/* Left: Log message and badges */}
                    <div className="flex-1 min-w-0">
                        {/* Primary: Log message */}
                        <div className="mb-2">
                            <div className="text-base font-semibold text-slate-900 dark:text-slate-100 mb-2">
                                {logMessage}
                            </div>
                            {/* Contact info for campaign message created - always show for this status */}
                            {log.status === 'campaign-message-created' && contact && contactWhatsappId && (
                                <div className="flex items-center gap-3 px-3 py-2 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
                                    <ContactAvatar
                                        contactId={contactWhatsappId}
                                        size={32}
                                        textSize={12}
                                        profilePicURL={contact.profilePicUrl}
                                        sx={{
                                            justifyContent: 'flex-start',
                                        }}
                                    />
                                    <div className="flex flex-col min-w-0 flex-1">
                                        <span className="text-sm font-semibold text-slate-900 dark:text-slate-100 truncate">
                                            {finalContactName}
                                        </span>
                                        {contact.mobile && (
                                            <span className="text-xs text-slate-600 dark:text-slate-400">
                                                {contact.mobile}
                                            </span>
                                        )}
                                        {contactWhatsappId && (
                                            <span className="text-xs text-slate-500 dark:text-slate-500 truncate">
                                                {contactWhatsappId}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                        
                        {/* Compact row: Status + Badges */}
                        <div className="flex items-center gap-2 flex-wrap">
                            <CampaignStatusBadge status={log.status} logMessage={logMessage} />
                            
                            {log.clientType && (
                                <Badge variant="outline" className="text-xs px-2 py-0.5 bg-purple-50 dark:bg-purple-900/50 text-purple-700 dark:text-purple-300 border-purple-300 dark:border-purple-700">
                                    {log.clientType}
                                </Badge>
                            )}

                            {deltaText && (
                                <Badge 
                                    variant="outline" 
                                    className="text-xs font-normal bg-blue-50 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 border-blue-300 dark:border-blue-700 px-2 py-0.5"
                                >
                                    {deltaText}
                                </Badge>
                            )}
                            
                            {/* Error icon */}
                            {hasError && (
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <div className="cursor-help">
                                            <AlertCircle className="h-4 w-4 text-red-600 dark:text-red-400" />
                                        </div>
                                    </TooltipTrigger>
                                    <TooltipContent className="max-w-md" side="top">
                                        <p className="text-xs break-words">
                                            {errorMessage && errorMessage.length > 100 
                                                ? `${errorMessage.substring(0, 100)}...` 
                                                : errorMessage}
                                        </p>
                                    </TooltipContent>
                                </Tooltip>
                            )}
                        </div>
                    </div>
                    
                    {/* Right: Timestamp */}
                    <div className="text-xs text-slate-500 dark:text-slate-400 whitespace-nowrap">
                        {moment(log.createdAt).format('MM/DD/YYYY, h:mm:ss A')}
                    </div>
                </div>

                {/* Expandable Details Panel */}
                {(log.clientId || log.metadata?.clientId || hasError || log.metadata?.initiatedBy || log.metadata?.previousStatus || contact || log.clientType) && (
                    <Collapsible open={isDetailsExpanded} onOpenChange={setIsDetailsExpanded}>
                        <CollapsibleTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-7 text-xs text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100">
                                {isDetailsExpanded ? (
                                    <>
                                        <ChevronUp className="h-3 w-3 mr-1" />
                                        Hide Details
                                    </>
                                ) : (
                                    <>
                                        <ChevronDown className="h-3 w-3 mr-1" />
                                        Show Details
                                    </>
                                )}
                            </Button>
                        </CollapsibleTrigger>
                        <CollapsibleContent className="mt-2 space-y-2">
                            <Separator className="bg-slate-200 dark:bg-slate-700" />
                            
                            {(log.clientId || log.metadata?.clientId) && (
                                <div className="flex items-center gap-2 text-sm">
                                    <span className="text-slate-600 dark:text-slate-300 font-medium">MQTT Client ID:</span>
                                    <code className="text-xs bg-slate-100 dark:bg-slate-700 px-2 py-1 rounded font-mono text-slate-700 dark:text-slate-200 break-all">
                                        {log.clientId || log.metadata?.clientId}
                                    </code>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-6 w-6 p-0"
                                        onClick={(e) => handleCopyId(e, log.clientId || log.metadata?.clientId || '')}
                                    >
                                        <Copy className={copiedId === (log.clientId || log.metadata?.clientId) ? "h-3 w-3 text-green-600 dark:text-green-400" : "h-3 w-3 text-slate-600 dark:text-slate-400"} />
                                    </Button>
                                </div>
                            )}

                            {log.metadata?.initiatedBy && (
                                <div className="flex items-center gap-2 text-sm">
                                    <span className="text-slate-600 dark:text-slate-300 font-medium">Initiated By:</span>
                                    <code className="text-xs bg-slate-100 dark:bg-slate-700 px-2 py-1 rounded font-mono text-slate-700 dark:text-slate-200">
                                        {log.metadata.initiatedBy}
                                    </code>
                                </div>
                            )}

                            {log.metadata?.previousStatus && (
                                <div className="text-sm text-slate-600 dark:text-slate-300">
                                    <span className="font-medium">Previous Status:</span> {log.metadata.previousStatus}
                                </div>
                            )}

                            {log.clientType && (
                                <div className="flex items-center gap-2 text-sm">
                                    <span className="text-slate-600 dark:text-slate-300 font-medium">Client Type:</span>
                                    <Badge variant="outline" className="text-xs">
                                        {log.clientType}
                                    </Badge>
                                </div>
                            )}

                            {contact && contactWhatsappId && (
                                <div className="space-y-2">
                                    <div className="text-sm font-medium text-slate-600 dark:text-slate-300">Contact Details:</div>
                                    <div className="flex items-center gap-3 pl-2">
                                        <ContactAvatar
                                            contactId={contactWhatsappId}
                                            size={40}
                                            textSize={14}
                                            sx={{
                                                justifyContent: 'flex-start',
                                            }}
                                        />
                                        <div className="flex-1 space-y-1">
                                            {finalContactName && (
                                                <div className="text-sm font-medium text-slate-900 dark:text-slate-100">
                                                    {finalContactName}
                                                </div>
                                            )}
                                            {contactWhatsappId && (
                                                <div className="flex items-center gap-2">
                                                    <span className="text-xs text-slate-600 dark:text-slate-400 font-medium">WhatsApp ID:</span>
                                                    <code className="text-xs bg-slate-100 dark:bg-slate-700 px-2 py-1 rounded font-mono text-slate-700 dark:text-slate-200">
                                                        {contactWhatsappId}
                                                    </code>
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        className="h-5 w-5 p-0"
                                                        onClick={(e) => handleCopyId(e, contactWhatsappId)}
                                                    >
                                                        <Copy className={copiedId === contactWhatsappId ? "h-3 w-3 text-green-600 dark:text-green-400" : "h-3 w-3 text-slate-600 dark:text-slate-400"} />
                                                    </Button>
                                                </div>
                                            )}
                                            {contact.mobile && (
                                                <div className="text-xs text-slate-500 dark:text-slate-400">
                                                    <span className="font-medium">Mobile:</span> {contact.mobile}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {hasError && errorMessage && (
                                <div className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded p-2">
                                    <div className="flex items-start gap-2">
                                        <strong>Error:</strong>
                                        <span className="flex-1 break-words">
                                            {errorMessage}
                                        </span>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            className="h-6 w-6 p-0 shrink-0"
                                            onClick={(e) => handleCopyId(e, errorMessage)}
                                        >
                                            <Copy className={copiedId === errorMessage ? "h-3 w-3 text-green-600 dark:text-green-400" : "h-3 w-3 text-slate-600 dark:text-slate-400"} />
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </CollapsibleContent>
                    </Collapsible>
                )}
            </div>
        </div>
    );
}
